import { Ref, UnwrapNestedRefs } from 'vue';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from 'vue-query';
import {
  createVoltaItem,
  deleteVoltaItem,
  getVoltaItem,
  getVoltaItems,
  updateVoltaItem,
} from '@/entities/volta-item/services';
import {
  VoltaItem,
  GetVoltaItemData,
  CreateVoltaItemData,
  UpdateVoltaItemData,
  DeleteVoltaItemData,
} from '@/entities/volta-item/types';
import { QueryFilters } from '@/types/services';
import { queryKeyHash } from '@/utils/query';

export const voltaItemsQueryName = 'voltaItems';

export const useGetVoltaItemsQuery = (
  options?: UseQueryOptions<VoltaItem[]>,
  filters?: Ref<QueryFilters<VoltaItem>>,
) =>
  useQuery(voltaItemsQueryName, () => getVoltaItems(filters?.value), {
    ...options,
    queryKeyHashFn: (key) => queryKeyHash(key, { filters: filters?.value }),
  });

export const useGetVoltaItemQuery = (
  data: Ref<GetVoltaItemData>,
  options?: UseQueryOptions<VoltaItem>,
) => useQuery([voltaItemsQueryName, data], () => getVoltaItem(data.value), options);

export const useCreateVoltaItemMutation = () => {
  const queryClient = useQueryClient();
  return useMutation((data: UnwrapNestedRefs<CreateVoltaItemData>) => createVoltaItem(data), {
    onSuccess(data) {
      queryClient.refetchQueries([voltaItemsQueryName, { id: data.id }], {active: true});
    },
  });
}

export const useUpdateVoltaItemMutation = () => {
  const queryClient = useQueryClient();
  return useMutation((data: UnwrapNestedRefs<UpdateVoltaItemData>) => updateVoltaItem(data), {
    onSuccess({ id }) {
      queryClient.refetchQueries(voltaItemsQueryName, {active: true});
      queryClient.refetchQueries([voltaItemsQueryName, id], {active: true});
    },
  });
};

export const useDeleteVoltaItemMutation = () => {
  const queryClient = useQueryClient();
  return useMutation((data: UnwrapNestedRefs<DeleteVoltaItemData>) => deleteVoltaItem(data), {
    onSuccess() {
      queryClient.refetchQueries(voltaItemsQueryName, {active: true});
    },
  });
};
